import React, { useEffect, useRef } from "react";

// A one-shot burst of confetti, sparkle stars, and glitter from a point
// on screen. Confetti tumbles (scaled by a spin angle) so it reads as 3D.
const COLORS = ["#5bcefa", "#f5a9b8", "#ffffff", "#ffe74c", "#c94f71", "#b48ae6", "#9fe0fb"];

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vrot: number;
  spin: number;
  vspin: number;
  size: number;
  color: string;
  kind: "confetti" | "star" | "glitter";
  life: number;
  maxLife: number;
};

export default function GlitterBurst({
  origin,
  onDone,
}: {
  origin: { x: number; y: number };
  onDone?: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx || reduce) {
      onDone?.();
      return;
    }

    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const particles: Particle[] = Array.from({ length: 170 }, (_, i) => {
      const kind = i % 5 === 0 ? "star" : i % 3 === 0 ? "glitter" : "confetti";
      // mostly upward, fanning out
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.3;
      const speed = 6 + Math.random() * 11;
      const maxLife = 90 + Math.random() * 70;
      return {
        x: origin.x,
        y: origin.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        rot: Math.random() * Math.PI * 2,
        vrot: (Math.random() - 0.5) * 0.3,
        spin: Math.random() * Math.PI * 2,
        vspin: 0.1 + Math.random() * 0.25,
        size: kind === "glitter" ? 2 + Math.random() * 2 : kind === "star" ? 6 + Math.random() * 6 : 6 + Math.random() * 5,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        kind,
        life: 0,
        maxLife,
      };
    });

    const drawStar = (size: number) => {
      // four-point sparkle
      ctx.beginPath();
      for (let i = 0; i < 8; i++) {
        const r = i % 2 === 0 ? size : size * 0.28;
        const a = (i * Math.PI) / 4;
        ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    };

    let frame = 0;
    const tick = () => {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      let alive = 0;
      for (const p of particles) {
        if (p.life > p.maxLife) continue;
        alive++;
        p.life++;
        p.vy += 0.28; // gravity
        p.vx *= 0.985; // air
        p.vy *= 0.985;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vrot;
        p.spin += p.vspin;
        const fade = Math.min(1, (p.maxLife - p.life) / 25);

        ctx.save();
        ctx.globalAlpha = fade;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.strokeStyle = "rgba(74, 52, 38, 0.6)";
        ctx.lineWidth = 1;

        if (p.kind === "confetti") {
          // tumbling: squash one axis by the spin to fake a 3D flip
          ctx.scale(1, Math.cos(p.spin));
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
          ctx.strokeRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        } else if (p.kind === "star") {
          const twinkle = 0.75 + 0.25 * Math.sin(p.life * 0.4);
          ctx.scale(twinkle, twinkle);
          drawStar(p.size);
        } else {
          ctx.globalAlpha = fade * (0.4 + 0.6 * Math.abs(Math.sin(p.life * 0.5)));
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
        }
        ctx.restore();
      }
      if (alive > 0) {
        frame = requestAnimationFrame(tick);
      } else {
        onDone?.();
      }
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", resize);
    };
    // runs once per burst
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        pointerEvents: "none",
        zIndex: 60,
      }}
    />
  );
}
